import {
	AdditionalInfoFieldType,
	AdditionalInfoType,
} from "node-library/dist/constants";
import {
	FrontendCompanySampleAdditionalInfoFields,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendSample,
	FrontendSampleAdditionalInfoMapping,
} from "node-library/dist/portal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import { GreenButton } from "../../../../common-components/button";
import {
	CommonSwitch,
	CustomDropdown,
	CustomTextInput,
} from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";

import { getTestCategoryMatrixMap } from "../../../../helpers/testCategoryHelper";
import { CustomDatePicker } from "../../../../common-components/datePicker";

interface Props {
	selectedTestCategory: FrontendCompanyTestCategory;

	editSample: (sample: FrontendSample) => void;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	sample: FrontendSample;
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function EditSampleModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { selectedTestCategory, loading, setLoading } = props;

	const { open, setOpen } = props;

	const [sampleName, setSampleName] = useState<string>(
		props.sample?.sampleName || "",
	);
	const [lotCode, setLotCode] = useState<string>(props.sample?.lotCode || "");

	const [sampleDescription, setSampleDescription] = useState<string>(
		props.sample?.sampleDescription || "",
	);

	const [sampleAdditionalInfos, setSampleAdditionalInfos] = useState<
		FrontendSampleAdditionalInfoMapping[]
	>(props.sample?.sampleAdditionalInfos || []);

	const [matrix, setMatrix] = useState<string>(props.sample?.matrix || "");
	const [selectedMatrixId, setSelectedMatrixId] = useState<string>(
		props.sample?.matrixId || "",
	);
	const categoryMatrixMap: Map<
		string,
		Array<FrontendCompanySampleMatrix>
	> = getTestCategoryMatrixMap(props.selectedTestCategory);
	const DEFAULT_MATRIX_ID: string =
		selectedTestCategory.sampleMatrices.length > 0
			? selectedTestCategory.sampleMatrices[0].matrixId
			: "";

	const [isCustomMatrix, setIsCustomMatrix] = useState<boolean>(
		props.sample?.isCustomMatrix || false,
	);

	const [sampleNameErrorMsg, setSampleNameErrorMsg] = useState<string>("");

	const [lotCodeErrorMsg, setLotCodeErrorMsg] = useState<string>("");

	const [matrixErrorMsg, setMatrixErrorMsg] = useState<string>("");

	const [sampleDescriptionErrorMsg, setSampleDescriptionErrorMsg] =
		useState<string>("");

	const [sampleAdditionalInfosErrorMsg, setSampleAdditionalInfosErrorMsg] =
		useState<Array<{ id: string; message: string }>>([]);

	const mandatoryFields: Array<any> = [
		{
			field: sampleName,
			predicates: [sampleName && sampleName.length > 0],
			errorMsgs: [translation("MissingInformation")],
			setError: setSampleNameErrorMsg,
		},
		{
			field: lotCode,
			predicates: [lotCode && lotCode.length > 0],
			errorMsgs: [translation("MissingInformation")],
			setError: setLotCodeErrorMsg,
		},

		{
			field: sampleDescription,
			predicates: [],
			errorMsgs: [],
			setError: setSampleDescriptionErrorMsg,
		},
		{
			field: matrix,
			predicates: [!(isCustomMatrix && matrix.trim().length == 0)],
			errorMsgs: [translation("MissingInformation")],
			setError: setMatrixErrorMsg,
		},
	];

	useEffect(() => {
		if (open) {
			for (const field of mandatoryFields) {
				field.setError("");
			}
			setSampleName(props.sample?.sampleName || "");

			setLotCode(props.sample?.lotCode || "");
			setSampleDescription(props.sample?.sampleDescription || "");

			if (props.sample.isCustomMatrix) {
				setMatrix(props.sample?.matrix);
			} else {
				const selectedMatrix: FrontendCompanySampleMatrix | undefined =
					selectedTestCategory.sampleMatrices.find((matrix) => {
						return matrix.matrixId == props.sample.matrixId;
					});

				setMatrix(selectedMatrix?.matrix || "");
			}
			setSelectedMatrixId(props.sample.matrixId || "");
			setIsCustomMatrix(props.sample.isCustomMatrix);
			setSampleAdditionalInfos(props.sample.sampleAdditionalInfos);
		}
	}, [open]);

	const handleSubmitSampleInfo = async () => {
		// Simple validation for required fields
		let hasError: boolean = false;
		for (const field of mandatoryFields) {
			field.setError("");
			for (let i = 0; i < field.predicates.length; i++) {
				if (!field.predicates[i]) {
					hasError = true;
					field.setError(field.errorMsgs[i]);
					break;
				}
			}
		}
		//check mandatory additional fields
		const mandatoryAdditionalFields: FrontendCompanySampleAdditionalInfoFields[] =
			selectedTestCategory.sampleAdditionalInfo.filter((info) => {
				return info.infoType === AdditionalInfoType.SAMPLE && info.mandatory;
			});
		for (const field of mandatoryAdditionalFields) {
			const matchingInfo = sampleAdditionalInfos.find((info) => {
				return field.fieldId === info.fieldId;
			});
			if (!matchingInfo || !matchingInfo.value) {
				//set error
				hasError = true;
				const updatedMsgs = sampleAdditionalInfosErrorMsg.map((existing) =>
					field.fieldId === existing.id
						? { ...existing, message: translation("MissingInformation") }
						: existing,
				);
				if (
					!sampleAdditionalInfosErrorMsg.find((msg) => {
						return msg.id === field.fieldId;
					})
				) {
					updatedMsgs.push({
						id: field.fieldId,
						message: translation("MissingInformation"),
					});
				}
				setSampleAdditionalInfosErrorMsg(updatedMsgs);
				break;
			}
		}
		if (!isCustomMatrix && !selectedMatrixId) {
			hasError = true;
			// setMatrixErrorMsg(translation("MissingInformation"));
		}

		if (hasError) {
			return;
		}

		const updatedSample: FrontendSample = {
			...props.sample,
			sampleName: sampleName.trim(),
			lotCode: lotCode.trim(),
			sampleDescription: sampleDescription.trim(),
			matrix,
			matrixId: selectedMatrixId,
			sampleAdditionalInfos,
			isCustomMatrix,
		};

		props.editSample(updatedSample);
	};

	const nameAndLotcodeInput: React.ReactNode = (
		<div className="sample-info-name-lotcode-row">
			<CustomTextInput
				id="new-sample-name-input"
				label={translation("SampleName")}
				value={sampleName}
				setValue={(value) => {
					setSampleName(value);
				}}
				placeholder={translation("SampleNameDescription")}
				required
				errorMsg={sampleNameErrorMsg}
				setErrorMsg={setSampleNameErrorMsg}
			/>
			<CustomTextInput
				id="new-sample-lotcode-input"
				label={translation("LotCode")}
				value={lotCode}
				setValue={(value) => {
					setLotCode(value);
				}}
				// description={translation("LotCodeDescription")}
				placeholder={translation("LotCodeDescription")}
				required
				errorMsg={lotCodeErrorMsg}
				setErrorMsg={setLotCodeErrorMsg}
			/>
		</div>
	);
	const testCategorySampleAdditionalInfoFields: Array<React.ReactNode> =
		selectedTestCategory.sampleAdditionalInfo
			.filter((info) => {
				return info.infoType === AdditionalInfoType.SAMPLE;
			})
			.map((info) => {
				const existingInfo = sampleAdditionalInfos.find((existing) => {
					return info.fieldId === existing.fieldId;
				});
				const value = existingInfo?.value || "";
				const setValueFunction = (value: string | Date | null) => {
					if (value == null) return;
					const updatedInfos: Array<FrontendSampleAdditionalInfoMapping> =
						sampleAdditionalInfos.map((existing) =>
							info.fieldId === existing.fieldId
								? { ...existing, value: value }
								: existing,
						);
					if (!existingInfo) {
						updatedInfos.push({
							fieldId: info.fieldId,
							value: value,
							infoType: AdditionalInfoType.SAMPLE,
							additionalFieldName: info.additionalFieldName,
							fieldType: info.fieldType,
						});
					}
					setSampleAdditionalInfos(updatedInfos);
				};
				switch (info.fieldType) {
					case AdditionalInfoFieldType.TEXT:
						return (
							<CustomTextInput
								id={`new-sample-${info.additionalFieldName}-input`}
								label={info.additionalFieldName}
								value={value as string}
								setValue={setValueFunction}
								// description={translation("LotCodeDescription")}
								required={info.mandatory}
								errorMsg={
									sampleAdditionalInfosErrorMsg.find((msg) => {
										return msg.id === info.fieldId;
									})?.message || ""
								}
								setErrorMsg={(value) => {
									const updatedMsgs = sampleAdditionalInfosErrorMsg.map(
										(existing) =>
											info.fieldId === existing.id
												? { ...existing, message: value }
												: existing,
									);
									if (
										!sampleAdditionalInfosErrorMsg.find((msg) => {
											return msg.id === info.fieldId;
										})
									) {
										updatedMsgs.push({
											id: info.fieldId,
											message: value,
										});
									}
									setSampleAdditionalInfosErrorMsg(updatedMsgs);
								}}
							/>
						);
					case AdditionalInfoFieldType.DATE:
						return (
							<div className="modal-datepicker-row">
								<div className="typography-body-01">
									{info.additionalFieldName}
								</div>

								<CustomDatePicker
									selectedDate={new Date(value)}
									setSelectedDate={setValueFunction}
									errorMsg={
										sampleAdditionalInfosErrorMsg.find((msg) => {
											return msg.id === info.fieldId;
										})?.message || ""
									}
								></CustomDatePicker>
							</div>
						);
				}
			});

	const sampleAdditionalInfoInput: React.ReactNode = (
		<div className="sample-info-name-lotcode-row">
			{testCategorySampleAdditionalInfoFields}
		</div>
	);

	function toggleCustomMatrixCheckbox() {
		if (isCustomMatrix) {
			//turning it off, set the default matrix and submatrix
			setSelectedMatrixId(DEFAULT_MATRIX_ID);
		} else {
			//turning custom matrix on, remove sub matrix
			setSelectedMatrixId("");
		}
		setIsCustomMatrix(!isCustomMatrix);
	}
	function handleMatrixChange(matrix: string) {
		const matchingMatrixEntries:
			| Array<FrontendCompanySampleMatrix>
			| undefined = categoryMatrixMap.get(matrix);

		if (!matchingMatrixEntries) return;
		setMatrix(matrix);
		setSelectedMatrixId(matchingMatrixEntries[0]?.matrixId);
	}
	const customMatrixField = {
		label: `${translation("CustomMatrix")}:`,
		description: ``,
		inputType: "checkbox",
		checked: isCustomMatrix,
		setChecked: () => toggleCustomMatrixCheckbox(),
	};
	const customMatrixSwitch: React.ReactNode = (
		<div className="common-card-header" style={{ margin: "10px 0px" }}>
			<div className="typography-heading-03">{translation("CustomMatrix")}</div>
			<CommonSwitch
				id={"additional-test-switch"}
				checked={customMatrixField.checked}
				setChecked={customMatrixField.setChecked}
			/>
		</div>
	);

	const matrixSelector: React.ReactNode = isCustomMatrix ? (
		<CustomTextInput
			id={`custom-matrix-textbox`}
			key={`custom-matrix-textbox`}
			label={translation("Matrix")}
			value={matrix}
			setValue={setMatrix}
			type="text"
			required={true}
			errorMsg={matrixErrorMsg}
			setErrorMsg={setMatrixErrorMsg}
		></CustomTextInput>
	) : (
		<CustomDropdown
			id="matrix-dropdown"
			label={translation("Matrix")}
			options={Array.from(categoryMatrixMap.keys()).map((value) => {
				return {
					value: value,
					title: value,
				};
			})}
			value={matrix}
			setValue={handleMatrixChange}
			hasError={!isCustomMatrix && !selectedMatrixId}
		></CustomDropdown>
	);

	const subMatrixOptions: Array<{
		value: string;
		title: string;
	}> = isCustomMatrix
		? []
		: (categoryMatrixMap.get(matrix) || []).map((value) => {
				const split = value.subMatrix.split(`${matrix} -`);

				const subMatrixTitle: string =
					split.length > 1 ? split[1].trim() : value.subMatrix;
				return {
					value: value.matrixId,
					title: subMatrixTitle,
				};
			});

	const subMatrixSelector: React.ReactNode = isCustomMatrix ? (
		<> </>
	) : (
		<CustomDropdown
			id="sub-matrix-dropdown"
			label={translation("SubMatrix")}
			options={subMatrixOptions}
			value={selectedMatrixId}
			setValue={(value) => {
				setSelectedMatrixId(value);
			}}
			hasError={!isCustomMatrix && !selectedMatrixId}
		></CustomDropdown>
	);
	const matrixSelectionRow: React.ReactNode = (
		<div>
			{customMatrixSwitch}

			<div key={"matrix-weight"} className="new-sample-matrix-selection-row">
				{matrixSelector}
				{subMatrixSelector}
			</div>
		</div>
	);

	const sampleDescriptionInput: React.ReactNode = (
		<CustomTextInput
			id="new-sample-weight-input"
			key="new-sample-weight-input"
			label={`${translation("SampleDescription")}`}
			value={sampleDescription}
			setValue={(value) => {
				setSampleDescription(value);
			}}
			placeholder={translation("SampleDescriptionDescription")}
			required
			isTextArea
			errorMsg={sampleDescriptionErrorMsg}
			setErrorMsg={setSampleDescriptionErrorMsg}
		/>
	);

	const editSampleModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={translation("EditSample")}
			// headerDescription={translation("ConfirmApprovalDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="submit-client-change-button"
					id="submit-client-change-button"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : (
							translation("Edit")
						)
					}
					disabled={loading}
					onClick={handleSubmitSampleInfo}
				></GreenButton>,
			]}
		>
			{nameAndLotcodeInput}
			{sampleAdditionalInfoInput}
			{matrixSelectionRow}
			{sampleDescriptionInput}
		</ModalWithButtons>
	);

	return editSampleModal;
}
